import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createUseStyles, Text, Icon, Link, Markdown, useTheme } from '@Sage/hoi-poi-ui';
import classNames from 'classnames';
import { useWeb5Context } from "../../../../../lib/Web5Context";
import { getMomentFromDateBackend, isSame, isToday, getDateToUserTimezone } from "../../../../../utils/time";
import { isEqual } from "../../../../../utils/objects";
import useBuildAnswer from "../../../hooks/useBuildAnswer";
import CustomLink from "./CustomLink";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'CopilotComment');
var _ref2 = /*#__PURE__*/React.createElement(Icon, {
  name: "infoOutlined"
});
const Comment = /*#__PURE__*/memo(_ref => {
  let {
    interactionId,
    text,
    isUser,
    isError,
    actionOnError,
    onClick,
    isWidget,
    isProgress,
    onCopy,
    onEmail,
    creationDate,
    vote,
    onVote,
    finished,
    disableVote,
    getChatBoxRef,
    tables,
    reports,
    avatar
  } = _ref;
  const boxRef = useRef(null);
  const classes = useStyles();
  const theme = useTheme();
  const {
    t
  } = useWeb5Context();
  const [isCopied, setIsCopied] = useState(false);
  const [enhancedText, setEnhancedText] = useState('');
  const {
    parseCopilotEmbeds
  } = useBuildAnswer();
  const stableTablesRef = useRef(tables);
  const stableReportsRef = useRef(reports);
  const [stableTables, setStableTables] = useState(tables);
  const [stableReports, setStableReports] = useState(reports);
  useEffect(() => {
    const tablesChanged = !isEqual(stableTablesRef.current, tables);
    const reportsChanged = !isEqual(stableReportsRef.current, reports);
    if (tablesChanged) {
      stableTablesRef.current = tables;
      setStableTables(tables);
    }
    if (reportsChanged) {
      stableReportsRef.current = reports;
      setStableReports(reports);
    }
  }, [tables, reports]);
  useEffect(() => {
    if (!text) {
      setEnhancedText('');
      return;
    }
    const enhanced = parseCopilotEmbeds(text);
    setEnhancedText(enhanced);
  }, [text, parseCopilotEmbeds]);
  const handleOnCopy = useCallback(() => {
    onCopy && onCopy();
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  }, [onCopy]);
  const handleOnEmail = useCallback(() => {
    var _boxRef$current;
    const textBox = (_boxRef$current = boxRef.current) === null || _boxRef$current === void 0 ? void 0 : _boxRef$current.querySelector('[class*="HoiPoi__CopilotComment__chatText"]');
    const htmlContent = textBox === null || textBox === void 0 ? void 0 : textBox.innerHTML;
    onEmail && onEmail(htmlContent);
  }, [onEmail]);
  const date = useMemo(() => {
    if (!creationDate) return '';
    const dateMoment = getMomentFromDateBackend(creationDate, 'DD/MM/YYYY HH:mm:ss', true);
    if (isToday(dateMoment)) return getDateToUserTimezone({
      date: dateMoment,
      outputFormat: 'HH:mm A'
    });else if (isSame(dateMoment, null, 'year')) return getDateToUserTimezone({
      date: dateMoment,
      outputFormat: 'DD/MM HH:mm A'
    });else return getDateToUserTimezone({
      date: dateMoment,
      outputFormat: 'DD/MM/YYYY HH:mm A'
    });
  }, [creationDate]);
  const showThumbUp = useMemo(() => {
    if (!onVote || disableVote) return false;
    if (isUser || !interactionId || !finished) return false;
    if (!vote || vote === '1') return true;
    return false;
  }, [onVote, disableVote, isUser, interactionId, finished, vote]);
  const showThumbDown = useMemo(() => {
    if (!onVote || disableVote) return false;
    if (isUser || !interactionId || !finished) return false;
    if (!vote || vote === '0') return true;
    return false;
  }, [onVote, disableVote, isUser, interactionId, finished, vote]);

  // Memoize markdown components with stable references
  const markdownComponents = useMemo(() => ({
    a: props => /*#__PURE__*/React.createElement(CustomLink, _extends({
      loading: !finished,
      tables: stableTables,
      reports: stableReports
    }, props))
  }), [finished, stableTables, stableReports]);

  // Memoize root classes
  const rootClasses = useMemo(() => classNames(classes.root, {
    'dana-chat-comment--clickable': !!onClick,
    'dana-chat-comment--copy': !isCopied && !isWidget && !!onCopy,
    'dana-chat-comment--copied': isCopied
  }), [classes.root, onClick, isCopied, isWidget, onCopy]);

  // Memoize text container classes
  const textContainerClasses = useMemo(() => classNames('dana-chat-comment-text-container', {
    'dana-chat-comment-user': isUser
  }), [isUser]);

  // Memoize markdown classes
  const markdownClasses = useMemo(() => classNames(classes.chatText, {
    [classes.chatTextEllipsis]: isWidget
  }), [classes.chatText, classes.chatTextEllipsis, isWidget]);

  // Memoize ref callback
  const handleRefCallback = useCallback(ref => {
    boxRef.current = ref;
    getChatBoxRef && getChatBoxRef({
      ref,
      interactionId
    });
  }, [getChatBoxRef, interactionId]);

  // Memoize vote handlers
  const handleThumbUp = useMemo(() => onVote && onVote('1'), [onVote]);
  const handleThumbDown = useMemo(() => onVote && onVote('0'), [onVote]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: rootClasses,
    onClick: onClick,
    ref: handleRefCallback
  }, interactionId && {
    id: interactionId
  }), /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-data"
  }, avatar && /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-avatar"
  }, avatar), /*#__PURE__*/React.createElement("div", {
    className: textContainerClasses
  }, !isWidget && /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-actions"
  }, date && /*#__PURE__*/React.createElement(Text, {
    className: "dana-chat-comment-date",
    type: "caption",
    color: theme.colors.utility.textSecondary
  }, date), !isProgress && /*#__PURE__*/React.createElement(React.Fragment, null, onCopy && /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-copy__icon",
    onClick: !isCopied ? handleOnCopy : undefined
  }, /*#__PURE__*/React.createElement(Icon, {
    name: isCopied ? 'done' : 'contentCopy'
  })), onEmail && !isUser && /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-email__icon",
    onClick: handleOnEmail
  }, /*#__PURE__*/React.createElement(Icon, {
    color: theme.colors.actionMinor[500],
    name: "email"
  })), showThumbUp && /*#__PURE__*/React.createElement("div", {
    className: classNames('dana-chat-comment-thumb__icon', {
      'dana-chat-comment__voted': vote === '1'
    }),
    onClick: handleThumbUp
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "thumbUpOutline",
    color: theme.colors.actionMinor[500]
  })), showThumbDown && /*#__PURE__*/React.createElement("div", {
    className: classNames('dana-chat-comment-thumb__icon', {
      'dana-chat-comment__voted': vote === '0'
    }),
    onClick: handleThumbDown
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "thumbDownOutline",
    color: theme.colors.actionMinor[500]
  })))), /*#__PURE__*/React.createElement(Markdown, {
    className: markdownClasses,
    content: enhancedText || text || '...',
    components: markdownComponents,
    overrides: {
      root: {
        skipHtml: true
      }
    }
  }))), isError && /*#__PURE__*/React.createElement("div", {
    className: "dana-chat-comment-error"
  }, _ref2, /*#__PURE__*/React.createElement(Text, {
    type: "body",
    color: theme.colors.utility.textSecondary
  }, t('label_dana_summary_error_not_sent')), /*#__PURE__*/React.createElement(Link, {
    variation: "primary",
    onClick: actionOnError
  }, t('action_dana_chat_error_try_again'))));
});
export default Comment;